import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { ServiceCommonType, ProcessType, ServiceAttendanceObject, ServiceObject, EstimatedTimeOfArrivalQuery, ServicesNodesAttributes, ServiceBillingInfo, OtherServiceListData } from './../@types/service.type';
import { ActionType, BillingType } from '../@types/service.type';
import {
  createService,
  getActionTypes,
  getAllServices,
  getBulkServicesByIds,
  getProcessTypes,
  getServiceActionProcessTypes,
  getOtherServicesUnderMainLocation,
  updateServiceById,
  getLinkedServices,
  updateParentService,
  updateReportedInformationByServiceId,
  getAllServiceAttendance,
  updateBillingInfo,
  getServiceById,
  getServicesUnderNodeId,
  updateServiceNodeAllocation,
  getEstimatedTimeOfArrival,
  getServicesNodesLocationRelatedAttributes,
  createOrUpdateServiceAttributes,
  getBillingTypes,
  getBillingInfo,
  updateBulkServiceList,
  updateBulkServiceEditList,
  createServiceBulk
} from '../api/services';
import { LinkedServiceData, ServiceAccordianData, ServiceListResponse } from '../@types';
import {
  AppActionTypesKey,
  AppLinkedServicesKey,
  AppProcessTypesKey,
  AppServiceActionProcessTypesKey,
  AppServicesKey,
  GetServiceKey,
  OtherServicesKey,
  ServiceAttendanceKey,
  ServiceBulkUpdateKey,
  ServiceAccordianKey,
  GetEstimatedTimeOfArrivalKey,
  ServicesNodesAttributesKey,
  ServiceBillingTypesKey,
  ServiceLogAllKey,
  ServiceBillingInfoKey
} from '../configs/services';
import { NodesAllocationByService } from '../configs/structures';
import { ColumnSelectionItem } from '../components/templates/ColumnSelection';

export const useAddService = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await createService(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey]
      });
    }
  });
};

export const useGetActionTypes = (): UseQueryResult<[ActionType]> => {
  return useQuery({
    queryKey: [AppActionTypesKey],
    queryFn: () => getActionTypes(),
    select: (response: any) => response,
    staleTime: 2 * 60 * 60 * 1000
  });
};

export const useGetProcessTypes = (): UseQueryResult<[ProcessType]> => {
  return useQuery({
    queryKey: [AppProcessTypesKey],
    queryFn: () => getProcessTypes(),
    select: (response: any) => response,
    staleTime: 2 * 60 * 60 * 1000
  });
};

export const useGetServiceActionProcessTypes = (activeOnly?: boolean): UseQueryResult<ServiceCommonType> => {
  return useQuery({
    queryKey: [AppServiceActionProcessTypesKey],
    queryFn: () => getServiceActionProcessTypes(activeOnly),
    select: (response: any) => response,
    staleTime: 2 * 60 * 60 * 1000
  });
};

export const useGetServicePaginatedData = (queryOption: any, columnSelectionItems: ColumnSelectionItem[]): UseQueryResult<ServiceListResponse> => {
  return useQuery({
    queryKey: [AppServicesKey, queryOption, columnSelectionItems],
    queryFn: () => getAllServices(queryOption, columnSelectionItems),
    gcTime: 0,
    enabled: false
  });
};

export const useUpdateService = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => updateServiceById(data.id, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [NodesAllocationByService, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id]
      });
    }
  });
};

export const useGetServiceById = (id: number): UseQueryResult<ServiceObject> => {
  return useQuery({
    queryKey: [GetServiceKey, id],
    queryFn: () => id && getServiceById(id),

    //https://tanstack.com/query/v4/docs/react/guides/window-focus-refetching
    refetchOnWindowFocus: false,

    select: (response: any) => response
  });
};

export const useUpdateReportedInformation = (): UseMutationResult => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => updateReportedInformationByServiceId(data.id, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey]
      });
    }
  });
};

export const useUpdateBulkServiceList = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => await updateBulkServiceList(data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceBulkUpdateKey]
      });
    }
  });
};

export const useUpdateBulkServiceEditList = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => await updateBulkServiceEditList(data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceBulkUpdateKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey]
      });
    }
  });
};

export const useGetBulkServicesByIds = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await getBulkServicesByIds(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceBulkUpdateKey]
      });
    }
  });
};

export const useGetOtherServicesUnderMainLocation = (nodeId: number, queryOption: any): UseQueryResult<OtherServiceListData> => {
  return useQuery({
    queryKey: [OtherServicesKey, nodeId, queryOption],
    queryFn: async () => nodeId !== 0 && getOtherServicesUnderMainLocation(nodeId, queryOption),
    select: (response: any) => response,
    gcTime: 0,
    enabled: false
  });
};

export const useGetLinkedServices = (serviceId: number): UseQueryResult<LinkedServiceData> => {
  return useQuery({
    queryKey: [AppLinkedServicesKey, serviceId],
    queryFn: () => serviceId && getLinkedServices(serviceId)
  });
};

export const useUpdateParentService = (serviceId: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => updateParentService(data.id, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey]
      });
      await queryClient.invalidateQueries({
        queryKey: [AppLinkedServicesKey, serviceId]
      });
    }
  });
};

export const useGetAllServiceAttendance: any = (serviceId: number): UseQueryResult<[ServiceAttendanceObject]> => {
  return useQuery({
    queryKey: [ServiceAttendanceKey, serviceId],
    queryFn: () => getAllServiceAttendance(serviceId)
  });
};

export const useUpdateBillingInfo = (serviceId: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => updateBillingInfo(serviceId, data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, serviceId]
      });
    }
  });
};

export const useGetServicesUnderNodeId = (nodeId: number, includeChildRecords: boolean): UseQueryResult<ServiceAccordianData[]> => {
  return useQuery({
    queryKey: [ServiceAccordianKey, nodeId],
    queryFn: () => getServicesUnderNodeId(nodeId, includeChildRecords),
    select: (response: any) => response
  });
};

export const useUpdateServiceAllocation = (id: number): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await updateServiceNodeAllocation(data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, id]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, id]
      });
    }
  });
};

export const useGetEstimatedTimeOfArrival = (queryOption: EstimatedTimeOfArrivalQuery): UseQueryResult<string> => {
  return useQuery({
    queryKey: [GetEstimatedTimeOfArrivalKey],
    queryFn: () => getEstimatedTimeOfArrival(queryOption),
    enabled: false
  });
};

export const useGetServicesNodesLocationRelatedAttributes = (serviceId: number, mainLocationNodeId: number, subLocationNodeId: number): UseQueryResult<ServicesNodesAttributes> => {
  return useQuery({
    queryKey: [ServicesNodesAttributesKey, serviceId],
    queryFn: () => serviceId && getServicesNodesLocationRelatedAttributes(serviceId, mainLocationNodeId, subLocationNodeId),
    enabled: false
  });
};

export const useCreateOrUpdateServiceAttributes = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await createOrUpdateServiceAttributes(data);
    },

    onSuccess: async (mutationResponse, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [ServicesNodesAttributesKey, parseInt(variables?.serviceId)]
      });
      await queryClient.invalidateQueries({
        queryKey: [GetServiceKey, parseInt(variables?.serviceId)]
      });
      await queryClient.invalidateQueries({
        queryKey: [NodesAllocationByService, parseInt(variables?.serviceId)]
      });
    }
  });
};

export const useGetServiceBillingTypes = (): UseQueryResult<[BillingType]> => {
  return useQuery({
    queryKey: [ServiceBillingTypesKey],
    queryFn: () => getBillingTypes(),
    select: (response: any) => response
  });
};

export const useGetBillingInfo = (serviceId: number): UseQueryResult<ServiceBillingInfo> => {
  return useQuery({
    queryKey: [ServiceBillingInfoKey, serviceId],
    queryFn: () => serviceId && getBillingInfo(serviceId),
    enabled: false
  });
};

export const useAddServiceBulk = (): UseMutationResult => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await createServiceBulk(data);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [AppServicesKey]
      });
    }
  });
};